import EventEmitter from 'eventemitter3';
import rawr from 'rawr';
import transport from 'rawr/transports/worker';

import testImage from './test_image.jpg?inline';

const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

/**
 * Returns test image used to prime face detection and segmentation
 */
const getTestImageData = () => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = testImage;
    img.onload = () => {
      const osc = new OffscreenCanvas(img.width, img.height);
      const offscreenCtx = osc.getContext('2d');
      offscreenCtx.drawImage(img, 0, 0);
      const imgData = offscreenCtx.getImageData(0, 0, img.width, img.height);
      resolve(imgData);
    };
    img.onerror = (err) => reject(err);
  });
};

/**
 * Creates a worker manager for Web Worker lifecycle and communication
 */
export function createWorkerManager(config) {
  const emitter = new EventEmitter();
  let worker = null;
  let rawrPeer = null;
  let ready = false;
  let workerLogBuffer = [];
  let workerLogHandler = null;

  // Worker RPC methods (populated after init)
  let workerMethods = {};

  /**
   * Wait for worker ready signal
   */
  const waitForReady = (timeout = 300000) => {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Worker initialization timeout'));
      }, timeout);

      emitter.once('ready', () => {
        clearTimeout(timer);
        resolve();
      });

      emitter.once('error', (error) => {
        clearTimeout(timer);
        reject(error);
      });
    });
  };

  /**
   * Initialize worker with rawr bidirectional communication
   */
  const init = async () => {
    if (worker) {
      throw new Error('Worker already initialized');
    }

    const workerDirectory = config.workerDirectory || 'public';

    try {
      try {
        worker = new Worker(`${workerDirectory}/worker.js?worker-directory=${encodeURIComponent(workerDirectory)}`);
      } catch (e) {
        emitter.emit('error', e);
      }

      worker.onerror = (error) => {
        console.error('Worker error:', error);
        emitter.emit('error', error);
      };

      // Set up rawr peer for bidirectional communication
      rawrPeer = rawr({ transport: transport(worker) });

      // Destructure worker methods for RPC calls
      const {
        mainRun,
        detectVitals,
        getVersionString,
        startSession,
        endSession,
        previewSession,
        getRunToken,
        authorizeVitals,
        setExtendedLogs,
        setFaceGuide,
        segmentImage,
        detectFace,
      } = rawrPeer.methods;

      // Store methods for external access
      workerMethods = {
        mainRun,
        detectVitals,
        getVersionString,
        startSession,
        endSession,
        previewSession,
        getRunToken,
        authorizeVitals,
        setExtendedLogs,
        setFaceGuide,
        segmentImage,
        detectFace,
      };

      // Listen for worker log messages
      rawrPeer.notifications.onlog((msg) => {
        if (workerLogHandler) {
          workerLogHandler(msg);
        } else {
          workerLogBuffer.push(msg);
        }
        emitter.emit('log', msg);
      });

      // Listen for worker ready signal
      rawrPeer.notifications.onready(async () => {
        if (!ready) {
          try {
            const imgData = await getTestImageData();
            await segmentImage(imgData, true);
            await detectFace(imgData);
          } catch (error) {
            console.error('Error initializing Mediapipe models', error, error.message);
          }

          ready = true;
          emitter.emit('ready');
        }
      });

      // Listen for signs messages
      rawrPeer.notifications.onsigns((data) => {
        emitter.emit('signsMessage', data);
      });

      // Listen for timing notifications
      rawrPeer.notifications.onsegDuration((data) => {
        emitter.emit('segmentationDuration', data);
      });

      rawrPeer.notifications.onfaceDuration((data) => {
        emitter.emit('faceDetectionDuration', data);
      });

      // Wait for ready signal
      try {
        await waitForReady();
      } catch (e) {
        console.log('failed calling waitForReady', e);
        throw (e);
      }
    } catch (error) {
      console.log({ error });
      throw new Error(`Failed to initialize worker: ${error}`);
    }
  };

  /**
   * Authorize use of vital core algorithm
   * @param {Function} authenticator - Callback function(runToken) that returns auth response
   */
  const authorizeVitalCore = async (authenticator) => {
    try {
      if (!authenticator || typeof authenticator !== 'function') {
        throw new Error('Authenticator callback is required');
      }

      // Get the run token from WASM
      const runToken = JSON.parse(await workerMethods.getRunToken());

      // Call the user-provided authenticator function with just the runToken
      const authResponse = await authenticator(runToken);

      if (!authResponse) {
        throw new Error('Authenticator returned no response');
      }

      // Pass the auth response to WASM
      let authorizedVitals = await workerMethods.authorizeVitals(JSON.stringify(authResponse));
      authorizedVitals = JSON.parse(authorizedVitals);
      return authorizedVitals;
    } catch (e) {
      console.log('error calling authorizeVitalCore in worker', e)
      throw e;
    }
  }

  /**
   * Call a worker method via rawr RPC
   */
  const callMethod = async (methodName, ...args) => {
    if (!worker || !rawrPeer) {
      throw new Error('Worker not initialized');
    }
    if (!workerMethods[methodName]) {
      throw new Error(`Method ${methodName} not available`);
    }
    return await workerMethods[methodName](...args);
  };

  /**
   * Send notification to worker (one-way message)
   */
  const notify = (notificationName, ...args) => {
    if (!worker || !rawrPeer) {
      throw new Error('Worker not initialized');
    }
    if (rawrPeer.notifiers[notificationName]) {
      rawrPeer.notifiers[notificationName](...args);
    } else {
      console.warn(`Notification ${notificationName} not available`);
    }
  };

  /**
   * Send notification to worker for iOS (one-way message)
   */
  const detectVitalsJS = (imgBitmap, width, height, timestamp) => {
    if (!worker || !rawrPeer) {
      throw new Error('Worker not initialized');
    }

    const frameInfo = {
      width,
      height,
      timestamp: timestamp
    }

    try {
      if (isIOS) {
        rawrPeer.notifiers.detectVitals(frameInfo, imgBitmap);
      } else {
        rawrPeer.notifiersExt.detectVitals(frameInfo, imgBitmap, { postMessageOptions: [imgBitmap] });
      }
    } catch (e) {
      console.log('error calling detectVitals', e);
    }
  };

  /**
   * Register a log handler
   */
  const onLog = (handler) => {
    workerLogHandler = handler;

    // Flush buffered logs
    const buffered = [...workerLogBuffer];
    workerLogBuffer = [];
    buffered.forEach(msg => handler(msg));
  };

  /**
   * Unregister log handler
   */
  const offLog = () => {
    workerLogHandler = null;
  };

  /**
   * Destroy worker
   */
  const destroy = () => {
    if (worker) {
      worker.terminate();
      worker = null;
      rawrPeer = null;
      ready = false;
      workerMethods = {};
    }
    emitter.removeAllListeners();
  };

  return {
    init,
    callMethod,
    notify,
    onLog,
    offLog,
    destroy,
    authorizeVitalCore,
    detectVitalsJS,
    on: emitter.on.bind(emitter),
    once: emitter.once.bind(emitter),
    off: emitter.off.bind(emitter),
    emit: emitter.emit.bind(emitter),
    // Expose rawr peer and methods for advanced usage
    get peer() { return rawrPeer; },
    get methods() { return workerMethods; },
  };
}
